import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { SessionStepProps, SessionStaticProps } from './SessionFlowBase';
import { makeStyles, IconButton, Tooltip, Button, Typography } from '@material-ui/core';
import { useHotkeys } from 'react-hotkeys-hook';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import WarningIcon from '@material-ui/icons/Warning';
import useHasPrivilege from '../Util/useHasPrivilege';
import { Privilege } from '../../api';

import { ClassType } from '../../api';
import useSession, { refreshSession } from '../Util/useSession';
import { LabelPosition, secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';
import sessionsPrepareService from '../../services/sessionsPrepare';
import SessionTeamSelectLabel from './SessionTeamSelectLabel';
import VideoLayout, { PanMethod } from '../VideoPlayer/VideoLayout';
import { VideoOverlayObject, Video, Coordinate } from '../VideoPlayer/VideoTypes';
import { Period, PeriodTypeToString } from './SessionPeriods';
import teamspecMoments, { LabelMoment, timeSpanToMs } from '../Util/teamspecMoments';
import { TSession } from './Sessions';
import { fetchPost } from '../Util/fetchAPI';
import { projection } from '../Util/calib';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    textAlign: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  text: {
    display: 'inline',
    marginRight: '10px',
  },
  "@keyframes myEffect": {
    "0%": {
      color: '#f00',
      transform: "scale(225%)"
    },
    "50%": {
      color: '#600',
      transform: "scale(100%)"
    },
    "100%": {
      color: '#f00',
      transform: "scale(225%)"
    },
  },
  icon: {
    width: '22px',
    height: '22px',
    margin: '4px',
  },
  warningIcon: {
    width: '22px',
    height: '22px',
    color: '#900',
    margin: '8px',
    animation: "$myEffect 1500ms infinite ease",
  },
  alert: {
    color: '#900',
    fontWeight: 'bold',
    backgroundColor: '#eee',
    border: '2px solid #900',
    margin: '4px',
    padding: '2px',
  },
  videoContainer: {
    width: '99%',
    height: '60vh',
  },
}));

interface Props extends SessionStepProps {}

export type SavePoint = LabelPosition;
type ProjectedSavePoint = {
  savePoint: SavePoint;
  imagePointsPerVideoId: Map<string, Coordinate>;
};

export type TeamLabel = {
  label: string;
  color: string;
  classType: ClassType;
  hotkey: string;
  requiredCount: number;
};

const firstFrameText: string = "This is the first frame of this period, beware of each team's position.";

const allTeamSpecMomentsDefined = (session: TSession | undefined | null, moments: LabelMoment[]) => {
  //only enable next button when we have all markers for each moment
  let allMomentsComplete: boolean = true;
  for (let moment of moments) {
    const momentSps: SavePoint[] | undefined = session?.labelPositions?.filter(
      (p: SavePoint) => moment.timestamp === timeSpanToMs(p.videoTime)
    );
    if (!momentSps || !moment) {
      allMomentsComplete = false;
      break;
    }

    let momentReqCount: number = 0;
    moment.labels.forEach((l: TeamLabel) => {
      momentReqCount += l.requiredCount;
    });

    allMomentsComplete = momentSps.length >= momentReqCount;
    if (!allMomentsComplete) {
      break;
    }
  }
  return allMomentsComplete;
};

export const labels = (session: TSession | undefined | null) => {
  return [
    {
      label: 'Goalkeeper ' + session?.homeTeam?.name,
      color: session?.homeKeeperColor as string,
      classType: ClassType.HomeKeeper,
      hotkey: '1',
      requiredCount: session?.keeperCount || 0,
    },
    {
      label: 'Player ' + session?.homeTeam?.name,
      color: session?.homePlayerColor as string,
      classType: ClassType.HomePlayer,
      hotkey: '2',
      requiredCount: session?.playerCount || 0,
    },
    {
      label: 'Player ' + session?.awayTeam?.name,
      color: session?.awayPlayerColor as string,
      classType: ClassType.AwayPlayer,
      hotkey: '3',
      requiredCount: session?.playerCount || 0,
    },
    {
      label: 'Goalkeeper ' + session?.awayTeam?.name,
      color: session?.awayKeeperColor as string,
      classType: ClassType.AwayKeeper,
      hotkey: '4',
      requiredCount: session?.keeperCount || 0,
    },
    {
      label: 'Official',
      color: session?.refereeColor as string,
      classType: ClassType.Referee,
      hotkey: '5',
      requiredCount: 3, //TODO; replace with data.session.nrReferees
    },
  ];
};

const SessionTeamSpecification: React.FunctionComponent<Props> & SessionStaticProps = ({
  sessionid,
  setNextEnabled,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { session } = useSession(sessionid);
  const [classType, setClassType] = useState<ClassType>(ClassType.Referee);
  const [isAddingSavePoint, setIsAddingSavePoint] = useState<boolean>(false);
  const [momentId, setMomentId] = useState<number>(0);
  const [adjustment, setAdjustment] = useState<number>(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [projectedSavePoints, setProjectedSavePoints] = useState<ProjectedSavePoint[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [projectionFailed, setProjectionFailed] = useState<boolean>(false);
  const isAllowedToChangeFrame = useHasPrivilege(Privilege.ExportData);

  const deleteLabelPositions = useCallback(
    (ids: number[]) => sessionsPrepareService.deleteLabelPositions(sessionid, ids).then(() => refreshSession(sessionid)),
    [sessionid]
  );

  const hasSelected = useMemo(() => {
    return session?.labelPositions?.find((value: SavePoint) => selected.indexOf(value.id) !== -1) !== undefined;
  }, [session, selected]);

  //get our match start
  let matchStart: number = 0;
  if (session?.periods && session.periods.length > 0) {
    const firstElement: Period = session.periods[0];
    if (firstElement) {
      matchStart = timeSpanToMs(firstElement.startTime);
    }
  }

  const teamLabels: TeamLabel[] = useMemo(() => {
    return labels(session);
  }, [session]);

  const moments = useMemo(() => {
    return teamspecMoments(session);
  }, [session]);
  const currentMoment: LabelMoment | undefined = moments[momentId];

  const momentSavePoints: SavePoint[] = useMemo(
    () => session?.labelPositions?.filter((p: SavePoint) => currentMoment?.timestamp === timeSpanToMs(p.videoTime)) || [],
    [currentMoment, session]
  );

  const getClassColor = useCallback(
    (classType: string | null | undefined) => {
      if (classType === ClassType.HomeKeeper && session?.homeKeeperColor) {
        return session.homeKeeperColor;
      } else if (classType === ClassType.HomePlayer && session?.homePlayerColor) {
        return session.homePlayerColor;
      } else if (classType === ClassType.AwayKeeper && session?.awayKeeperColor) {
        return session.awayKeeperColor;
      } else if (classType === ClassType.AwayPlayer && session?.awayPlayerColor) {
        return session.awayPlayerColor;
      } else if (classType === ClassType.Referee && session?.refereeColor) {
        return session.refereeColor;
      }
      //if we didnt find the class, or color isnt set..
      return '#000';
    },
    [session]
  );

  const addSavePoint = useCallback(
    (videoId: string, coordinate: Coordinate) => {
      if (!isAddingSavePoint) return;
      const currentCTCount = momentSavePoints.filter((sp) => sp.classType === classType).length;
      const maxCTCount = currentMoment?.labels.find((l) => l.classType === classType)?.requiredCount || 0;
      if (currentCTCount >= maxCTCount) {
        setAlertMessage(
          'We reached our maximum labels for this class. Remove a point of this class before adding a new one.'
        );
        return;
      }
      setAlertMessage(undefined);

      const video: Video | undefined | null = session?.videos?.find((vid: Video) => {
        return String(vid.id) === videoId;
      });
      if (!video) return;

      const calib = JSON.parse(video?.calibration || '');

      //get the 3d position from clicked point
      fetchPost('/gateway/calibrations/reprojection', {
        calibration: calib,
        camera_coordinates: [[coordinate.x, coordinate.y]],
      }).then((data: number[][]) => {
        const result = projection(calib, data);
        const invalidResult: boolean = result[0][0] === -1;
        if(invalidResult) {
          enqueueSnackbar('Labeled point is invalid.', { variant: 'error', autoHideDuration: 3000 });
          return;
        }
        sessionsPrepareService
          .addLabelPosition(sessionid, {
            videoTime: secondsToTimeSpan((currentMoment.timestamp || 0) / 1000),
            x: data[0][0],
            y: data[0][1],
            z: 0,
            classType: classType,
          })
          .then(() => refreshSession(sessionid));
      });
    },
    [isAddingSavePoint, enqueueSnackbar, momentSavePoints, currentMoment, session, classType, sessionid]
  );

  const toggleIsAddingSavePoint = useCallback(() => {
    setIsAddingSavePoint((prevIsAddingSavePoint) => !prevIsAddingSavePoint);
  }, []);

  const togglePointSelect = useCallback(
    (pointId: number) => {
      if (selected.indexOf(pointId) !== -1) {
        setSelected((prev) => prev.filter((s) => s !== pointId));
      } else {
        setSelected((prev) => [...prev, pointId]);
      }
    },
    [selected]
  );

  const removeSelectedPoints = useCallback(() => {
    deleteLabelPositions(selected);
    setSelected([]);
  }, [sessionid, deleteLabelPositions, selected]);

  const nextMoment = useCallback(() => {
    setAlertMessage(undefined);
    if (momentId === moments.length - 1) {
      return;
    }
    setMomentId((prevId) => prevId + 1);
    setAdjustment(0);
  }, [momentId, moments.length]);

  const nextFrame = useCallback(() => {
    const newAdjustment = adjustment+ 100;
    setAdjustment(newAdjustment);
  }, [adjustment]);

  const previousFrame = useCallback(() => {
    const newAdjustment = adjustment- 100;
    setAdjustment(newAdjustment);
  }, [adjustment]);

  const previousMoment = useCallback(() => {
    if (momentId === 0) return;
    setMomentId((prevId) => prevId - 1);
    setAdjustment(0);
  }, [momentId]);

  //hotkeys
  useHotkeys('a', toggleIsAddingSavePoint, { keydown: true, keyup: false }, [toggleIsAddingSavePoint]);
  useHotkeys('d', removeSelectedPoints, { keydown: true, keyup: false }, [removeSelectedPoints]);
  useHotkeys('z', previousMoment, { keydown: true, keyup: false }, [previousMoment]);
  useHotkeys('x', nextMoment, { keydown: true, keyup: false }, [nextMoment]);

  useEffect(() => {
    //save the points in sessionProps everytime it changes
    setNextEnabled(allTeamSpecMomentsDefined(session, moments));
  }, [session, matchStart, moments, setNextEnabled]);

  const sessionTeamSelectLabels = useMemo(
    () =>
      teamLabels.map((t: TeamLabel) => {
        const teamCount: number = momentSavePoints.filter(
          (value: SavePoint) =>
            value.classType === t.classType && currentMoment && timeSpanToMs(value.videoTime) === currentMoment.timestamp
        ).length;
        const currentLabel: TeamLabel | undefined =
          currentMoment && currentMoment.labels.find((tl) => tl.classType === t.classType);

        return (
          <span key={t.hotkey}>
            <SessionTeamSelectLabel
              session={session || undefined}
              label={currentLabel?.label || t.label}
              count={teamCount}
              requiredCount={currentLabel?.requiredCount || 0}
              color={t.color}
              classType={t.classType}
              isSubstitution={(currentMoment.isSubstitution && !!currentLabel?.requiredCount) || false}
              isActive={classType === t.classType}
              isEnabled={currentLabel !== undefined}
              hotkey={t.hotkey}
              setClassType={setClassType}
            />
          </span>
        );
      }),
    [classType, session, currentMoment, momentSavePoints, teamLabels]
  );

  useEffect(() => {
    if (moments.length > 0) {
      const ips = session?.labelPositions
        ?.filter((ip) => moments.find((moment) => moment.timestamp === timeSpanToMs(ip.videoTime)) === undefined)
        ?.map((ip) => ip.id);
      if (ips && ips.length > 0) {
        deleteLabelPositions(ips);
      }
    }
  }, [session, deleteLabelPositions, moments]);

  const matchTime = useMemo(() => {
    if (session?.periods) {
      const periods = [...session.periods];
      periods.sort((a, b) => timeSpanToMs(a.startTime) - timeSpanToMs(b.startTime));
      const addSeconds = timeSpanToSeconds(session.startAndFinishOffset) || 3;
      //Find the period in which this moment falls
      for (const p of periods) {
        if (currentMoment.timestamp <= timeSpanToMs(p.endTime) + addSeconds) {
          const ms = currentMoment.timestamp - timeSpanToMs(p.startTime);
          const negative = ms < 0;
          let seconds = Math.abs(ms / 1000);
          const minutes = Math.floor(seconds / 60);
          seconds = seconds - minutes * 60;
          return (
            PeriodTypeToString(p.type) +
            ' ' +
            (negative ? '-' : '') +
            ('0' + Math.abs(minutes)).slice(-2) +
            ':' +
            ('0' + Math.abs(seconds)).slice(-2)
          );
        }
      }
    }
    return '-';
  }, [currentMoment, session]);

  const projectPoints = useCallback(() => {
    // if we have no label positions, reset save points array instead
    if (!session?.labelPositions || session.labelPositions.length === 0) {
      setProjectedSavePoints([]);
      return;
    }

    const newval = session.labelPositions.map((sp: SavePoint) => {
      return {
        savePoint: sp,
        imagePointsPerVideoId: new Map<string, Coordinate>(),
      };
    });

    session?.videos?.forEach((video: Video) => {
      // convert the 3d position to the 2d position
      const calib = JSON.parse(video?.calibration || '');
      const worldPositions: number[][] = [];
      const labelPositions: SavePoint[] = session?.labelPositions || [];

      labelPositions.forEach((value: SavePoint) => {
        const pos: number[] = [value.x || 0, value.y || 0, value.z || 0];
        worldPositions.push(pos);
      });
      const projectedData = projection(calib, worldPositions);
      if(!projectedData) {
        setProjectionFailed(true);
        return;
      }
      let i: number = 0;
      newval.forEach((psp: ProjectedSavePoint) => {
        let coord = projectedData[i];
        psp.imagePointsPerVideoId.set(String(video.id), {
          x: coord[0],
          y: coord[1],
        });
        i += 1;
      });
    });
    setProjectedSavePoints(newval);
  }, [session]);

  useEffect(() => {
    projectPoints();
  }, [projectPoints]);

  useEffect(() => {
    if(projectionFailed) {
      setTimeout(() => {
        setProjectionFailed(false);
        projectPoints();
      }, 3000);
    }
  }, [projectPoints, projectionFailed]);

  const overlayItems = new Map<string, VideoOverlayObject[]>();
  session?.videos?.forEach((video: Video) => {
    const viewSavePoints: ProjectedSavePoint[] =
      projectedSavePoints.filter(
        (value: ProjectedSavePoint) => timeSpanToMs(value.savePoint.videoTime) === currentMoment.timestamp
      ) || [];

    const viewOverlayObjects: VideoOverlayObject[] = [];
    viewSavePoints.forEach((value: ProjectedSavePoint) => {
      const projectedCoord: Coordinate | undefined = value.imagePointsPerVideoId.get(String(video.id));
      if (!projectedCoord) return;

      const isSelected: boolean = selected.indexOf(value.savePoint.id) !== -1;
      viewOverlayObjects.push({
        svg: (
          <svg viewBox="0 0 14 14">
            <circle
              cx={7}
              cy={7}
              r={5}
              fill={getClassColor(value.savePoint.classType)}
              stroke={isSelected ? '#fff6a9' : '#000'}
              strokeWidth={isSelected ? 2 : 1}
              onClick={() => togglePointSelect(value.savePoint.id)}
            />
          </svg>
        ),
        width: 14,
        height: 14,
        center: projectedCoord,
      });
    });

    overlayItems.set(String(video.id), viewOverlayObjects);
  });

  const mouseOverlay: VideoOverlayObject | undefined = isAddingSavePoint
    ? {
        svg: <circle cx={5} cy={5} r={5} fill={getClassColor(classType)} stroke="#000" strokeWidth={1} />,
        width: 10,
        height: 10,
        center: { x: 0, y: 0 },
      }
    : undefined;

    const currentTimestamp = currentMoment.timestamp + adjustment;

  return (
    <CenterContainer>
      <Container>
        <Wrapper>
          <span className={classes.row}>
            {sessionTeamSelectLabels}
            <div style={{ flexGrow: 1 }}></div>
            <Time>    
              { 
                currentMoment.isFirstOfPeriod && <Tooltip title={firstFrameText} arrow>
                  <WarningIcon className={classes.warningIcon} />
                </Tooltip>
              }
              { matchTime }
            </Time>
            <Tooltip title="Add point on the ground between the feet [A-key]" arrow>
              <IconButton onClick={toggleIsAddingSavePoint}>
                {isAddingSavePoint ? <AddCircleIcon /> : <AddCircleOutlineIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove selected points [D-key]" arrow>
              <IconButton onClick={removeSelectedPoints}>
                {hasSelected ? <DeleteIcon /> : <DeleteOutlineIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Go to previous moment [Z-key]" arrow>
              <Button onClick={previousMoment}>&lt;</Button>
            </Tooltip>
            <Typography style={{ margin: 'auto' }}>
              {momentId + 1} / {moments.length}
            </Typography>
            <Tooltip title="Go to next moment [X-key]" arrow>
              <Button onClick={nextMoment}>&gt;</Button>
            </Tooltip>
          </span>
          {isAllowedToChangeFrame && <span className={classes.row}>
            <Tooltip title="Go to previous frame" arrow>
              <Button onClick={previousFrame}>&lt;</Button>
            </Tooltip>
            <Tooltip title="Adjustment" arrow>
                <span style={{marginTop: '10px'}}>{currentTimestamp}/{adjustment}</span>
              </Tooltip> 
            <Tooltip title="Go to next frame" arrow>
              <Button onClick={nextFrame}>&gt;</Button>
            </Tooltip>
          </span>}
          {
            alertMessage && <div className={classes.alert}>
              {alertMessage}
            </div>
          }
          <div className={classes.videoContainer}>
            <VideoLayout
              videos={session?.videos || []}
              showOverlayUI={false}
              playTime={Math.round(currentTimestamp*0.001)}
              onViewMouseDown={addSavePoint}
              viewOverlays={overlayItems}
              mouseOverlay={mouseOverlay ? [mouseOverlay] : []}
              noFullscreen
              overridePanMethod={PanMethod.Drag}
            />
          </div>
        </Wrapper>
      </Container>
    </CenterContainer>
  );
};

const Time = styled.div`
  display: flex;
  align-items: center;
`;

const CenterContainer = styled.div`
  display: table-cell;
  height: calc(100vh - 350px);
  text-align: center;
  vertical-align: middle;
  width: 100%;
`;

const Container = styled.div`
  margin: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

SessionTeamSpecification.isComplete = (session: TSession) => {
  const moments = teamspecMoments(session);
  return allTeamSpecMomentsDefined(session, moments);
};

export default SessionTeamSpecification;
